import { useEffect, useMemo, useState } from 'react';
import type { BrowserType } from '../../assets/browser';
import { getBrowserIcon } from '../../assets/browser';
import Forward from '../../assets/icons/Forward';
import CustomQRCode from '../../components/CustomQRCode';
import { PageContent } from '../../components/modal/styles';
import { ForwardContainer, WalletItem } from '../../components/wallets/styles';
import useLocales from '../../hooks/useLocales';
import { type WalletProps } from '../../hooks/useWalletsProps';
import { formatString } from '../../locales';
import { Links, Subtitle, Tip, Title, WalletIconContainer, Wrapper } from './styles';

enum ViewType {
  List = 'List',
  QRCode = 'QRCode',
  Unavailable = 'Unavailable',
}

const Page = ({ wallet }: { wallet: WalletProps }) => {
  const locales = useLocales();
  const [viewType, setViewType] = useState<ViewType>();
  const downloadUrls = useMemo(() => {
    if (wallet?.downloadUrls) {
      return Object.keys(wallet.downloadUrls)
        .map((key) => {
          const url = wallet?.downloadUrls?.[key] || '';
          if (wallet.id === 'braveWallet' && key === 'website') {
            key = 'brave';
          }
          return {
            icon: getBrowserIcon(key as BrowserType),
            name: key,
            install: formatString(locales.installExtension, key?.charAt(0).toUpperCase() + key?.slice(1)),
            url,
          };
        })
        .filter((item) => {
          if (wallet.id === 'braveWallet') {
            return !['ios', 'android'].includes(item.name);
          }
          return !['ios', 'android', 'website'].includes(item.name);
        });
    } else {
      return [];
    }
  }, [wallet, locales]);

  useEffect(() => {
    const { website } = wallet?.downloadUrls || {};
    if (downloadUrls?.length) {
      setViewType(ViewType.List);
    } else if (website) {
      setViewType(ViewType.QRCode);
    } else {
      setViewType(ViewType.Unavailable);
    }
  }, [wallet, downloadUrls]);

  return (
    <PageContent style={{ marginTop: 0 }}>
      <Wrapper>
        {viewType === ViewType.List && <WalletIconContainer>{wallet?.icon}</WalletIconContainer>}
        <Title>{formatString(locales.installToConnect, wallet?.name || '')}</Title>
        {viewType === ViewType.List && <Subtitle>{locales.selectPreferredOptions}</Subtitle>}

        <div
          style={{
            display: viewType !== ViewType.List ? 'none' : 'block',
          }}
        >
          <Links>
            {downloadUrls.map((browser, index) => (
              <WalletItem key={index} onClick={() => window.open(browser.url, '_blank')}>
                <div className='btn-wrap'>
                  {browser.icon?.()}
                  {browser.install}
                </div>
                <ForwardContainer>
                  <Forward />
                </ForwardContainer>
              </WalletItem>
            ))}
          </Links>
        </div>

        {viewType === ViewType.QRCode && (
          <div
            style={{
              padding: '30px',
            }}
          >
            <CustomQRCode value={wallet?.downloadUrls?.website} ecl='H' image={wallet?.icon} />
          </div>
        )}

        <>
          {viewType === ViewType.List && <Tip>{locales.refreshPageOnceInstalled}</Tip>}
          {viewType === ViewType.QRCode && <Tip>{locales.walletOnlyAvailableOnMobile}</Tip>}
        </>
      </Wrapper>
    </PageContent>
  );
};

export default Page;
